import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle } = FiIcons;

const WhyChooseUs = () => {
  const coreValues = [
    {
      title: 'Tailored Recovery Plans',
      desc: 'We don’t believe in one-size-fits-all. Every body is different, and every treatment plan is unique.'
    },
    {
      title: 'Evidence-Based Care',
      desc: 'Our therapists use the latest clinical research and techniques to ensure your recovery is efficient.'
    },
    {
      title: 'Hands-On Approach',
      desc: 'We combine manual therapy with guided exercise to address the root cause of your pain.'
    },
    {
      title: 'Modern Facility',
      desc: 'Train and recover in a comfortable, state-of-the-art environment equipped for all levels of mobility.'
    }
  ];

  return (
    <section id="why-choose-us" className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-emerald-600 font-bold tracking-wider uppercase mb-2 block text-sm">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Core Values & <br/>
              <span className="text-teal-600">Philosophy</span>
            </h2>
            <p className="text-slate-600 mb-8 leading-relaxed text-base lg:text-lg">
              We are dedicated to providing the best possible care for our patients in Surat. Our team of experts is here to help you every step of the way.
            </p>

            <div className="space-y-4 lg:space-y-6">
              {coreValues.map((item, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex bg-slate-50 p-4 rounded-xl border border-slate-100 hover:border-emerald-200 transition-colors hover:shadow-md"
                >
                  <div className="mr-4 mt-1">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Image Composition */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[300px] lg:h-full lg:min-h-[500px] mt-8 lg:mt-0"
          >
            <motion.div 
              className="absolute top-0 right-0 w-full lg:w-4/5 h-4/5 z-10"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              {/* Reliable Unsplash ID: Modern Doctor Office */}
              <img 
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&q=80&w=1000" 
                alt="Therapy Session" 
                className="w-full h-full object-cover rounded-2xl shadow-2xl" 
              />
            </motion.div>
            <motion.div 
              className="hidden lg:block absolute bottom-0 left-0 w-3/5 h-3/5 border-8 border-white rounded-2xl shadow-xl overflow-hidden z-20"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
            >
              {/* Reliable Unsplash ID: Medical Equipment */}
              <img 
                src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&q=80&w=600" 
                alt="Modern Equipment" 
                className="w-full h-full object-cover" 
              />
            </motion.div>
            {/* Decorative Shape */}
            <div className="absolute top-10 left-10 w-32 h-32 bg-emerald-100 rounded-full -z-10 blur-2xl opacity-60 animate-pulse"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;